<template>
	<div class="wa-chat">
		<div ref="scroll" class="wa-chat-body" data-tour="timeline">
			<template v-for="(riga, i) in righe">
				<div v-if="riga.giorno" :key="'g' + i" class="wa-day">
					<span>{{ riga.giorno }}</span>
				</div>
				<div v-else :key="riga.msg._id || riga.msg.id || i" class="wa-row" :class="riga.uscente ? 'wa-row--out' : 'wa-row--in'">
					<div class="wa-bubble" :class="riga.uscente ? 'wa-bubble--out' : 'wa-bubble--in'">
						<div v-if="riga.msg.type === 'template'" class="wa-template">
							<v-icon x-small class="mr-1">mdi-text-box-outline</v-icon>{{ riga.msg.template && riga.msg.template.name }}
						</div>
						<Media v-else :item="riga.msg" class="wa-media" />
						<div class="wa-meta">
							<span>{{ riga.ora }}</span>
							<v-icon v-if="riga.uscente" small :color="riga.spunta.color" :title="riga.spunta.label" class="ml-1">{{ riga.spunta.icon }}</v-icon>
						</div>
					</div>
				</div>
			</template>
			<div v-if="!righe.length" class="wa-empty">Nessun messaggio</div>
		</div>

		<div class="wa-composer" data-tour="reply">
			<div v-if="post.attachs.length" class="wa-attachs">
				<v-chip v-for="(attach, index) of post.attachs" :key="'a' + index" small close class="mr-1 mb-1" @click:close="$emit('remove-attach', index)">
					<v-icon left small>mdi-paperclip</v-icon>
					<span class="text-truncate wa-chip-text">{{ nomeFile(attach) }}</span>
				</v-chip>
			</div>
			<div class="wa-composer-row">
				<v-menu top offset-y>
					<template v-slot:activator="{ on, attrs }">
						<v-btn icon class="wa-tool" v-bind="attrs" v-on="on" :title="'Tipo messaggio: ' + (post.type || 'automatico')">
							<v-icon>{{ iconaTipoPost }}</v-icon>
						</v-btn>
					</template>
					<v-list dense>
						<v-list-item v-for="t in tipi" :key="t.value" @click="post.type = t.value">
							<v-list-item-icon class="mr-3"><v-icon small>{{ t.icon }}</v-icon></v-list-item-icon>
							<v-list-item-title :class="{ 'primary--text': post.type === t.value }">{{ t.text }}</v-list-item-title>
						</v-list-item>
					</v-list>
				</v-menu>
				<v-btn icon class="wa-tool" title="Allega file" :loading="uploading" @click="$refs.file.click()">
					<v-icon>mdi-paperclip</v-icon>
				</v-btn>
				<input ref="file" type="file" class="d-none" @change="onFile" />
				<v-textarea
					v-model="post.text"
					placeholder="Messaggio"
					rows="1"
					auto-grow
					dense
					solo
					flat
					hide-details
					class="wa-input"
					@keydown.enter.exact.prevent="invia"
				></v-textarea>
				<v-btn fab small depressed color="primary" class="wa-send" :disabled="!puoInviare" title="Invia" @click="invia">
					<v-icon>mdi-send</v-icon>
				</v-btn>
			</div>
		</div>
	</div>
</template>

<script>
import { isOutgoing, statusIcon, toDate } from './chat-helpers';

const Media = () => import('./media.vue');

const TIPI = [
	{ value: '', text: 'Automatico', icon: 'mdi-message-text-outline' },
	{ value: 'text', text: 'Testo', icon: 'mdi-format-text' },
	{ value: 'image', text: 'Immagine', icon: 'mdi-camera' },
	{ value: 'video', text: 'Video', icon: 'mdi-video' },
	{ value: 'audio', text: 'Audio', icon: 'mdi-microphone' },
	{ value: 'document', text: 'Documento', icon: 'mdi-file-document-outline' },
];

// conversazione in stile WhatsApp: fumetti a sinistra (cliente) e a destra (noi) + barra di risposta
export default {
	name: 'ChatThread',
	components: { Media },
	props: {
		// conversazione: messages arriva dallo store dal piu' recente al piu' vecchio
		item: { type: Object, required: true },
		uploading: { type: Boolean, default: false },
	},
	data() {
		return { tipi: TIPI };
	},
	computed: {
		post() {
			return this.item.post;
		},
		iconaTipoPost() {
			const t = TIPI.find(x => x.value === this.post.type);
			return t ? t.icon : TIPI[0].icon;
		},
		puoInviare() {
			return !!((this.post.text && this.post.text.trim()) || this.post.attachs.length);
		},
		righe() {
			// dal piu' vecchio al piu' recente, come su WhatsApp (l'ordine dell'array non e' affidabile)
			const messaggi = (this.item.messages || []).slice().sort((a, b) => (parseInt(a.timestamp, 10) || 0) - (parseInt(b.timestamp, 10) || 0));
			const righe = [];
			let ultimoGiorno = '';
			messaggi.forEach(msg => {
				const data = toDate(msg.timestamp);
				const giorno = data ? this.etichettaGiorno(data) : '';
				if (giorno && giorno !== ultimoGiorno) {
					righe.push({ giorno });
					ultimoGiorno = giorno;
				}
				righe.push({
					msg,
					uscente: isOutgoing(msg, this.item.contact),
					spunta: statusIcon(msg.message_status),
					ora: data ? this.$moment(data).format('HH:mm') : '',
				});
			});
			return righe;
		},
	},
	watch: {
		'item.messages.length'() {
			this.$nextTick(this.scrollInFondo);
		},
	},
	mounted() {
		this.$nextTick(this.scrollInFondo);
		// Media e' async e le immagini cambiano altezza dopo il caricamento
		setTimeout(this.scrollInFondo, 300);
		this.$refs.scroll.addEventListener('load', this.scrollInFondo, true);
	},
	beforeDestroy() {
		this.$refs.scroll.removeEventListener('load', this.scrollInFondo, true);
	},
	methods: {
		scrollInFondo() {
			const el = this.$refs.scroll;
			if (el) el.scrollTop = el.scrollHeight;
		},
		etichettaGiorno(data) {
			const m = this.$moment(data);
			if (m.isSame(this.$moment(), 'day')) return 'Oggi';
			if (m.isSame(this.$moment().subtract(1, 'day'), 'day')) return 'Ieri';
			return m.format('DD/MM/YYYY');
		},
		nomeFile(attach) {
			const url = (attach && attach.url) || attach || '';
			return String(url).split('/').pop();
		},
		tipoDaAllegato() {
			const nome = this.nomeFile(this.post.attachs[0]).toLowerCase();
			const ext = nome.split('.').pop();
			if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image';
			if (['mp4', '3gp', 'mov'].includes(ext)) return 'video';
			if (['mp3', 'ogg', 'aac', 'amr', 'm4a', 'opus'].includes(ext)) return 'audio';
			return 'document';
		},
		onFile(event) {
			const file = event.target.files && event.target.files[0];
			if (file) this.$emit('upload', file);
			event.target.value = '';
		},
		invia() {
			if (!this.puoInviare) return;
			// tipo "automatico": dedotto dall'allegato, altrimenti testo
			if (!this.post.type) {
				this.post.type = this.post.attachs.length ? this.tipoDaAllegato() : 'text';
			}
			this.$emit('send');
		},
	},
};
</script>

<style scoped>
.wa-chat {
	border: 1px solid rgba(0, 0, 0, 0.12);
	border-radius: 10px;
	overflow: hidden;
	background: #efeae2;
	max-width: 820px;
	margin: 0 auto;
}
.wa-chat-body {
	max-height: 480px;
	overflow-y: auto;
	padding: 10px 12px;
	-webkit-overflow-scrolling: touch;
}
.wa-day {
	text-align: center;
	margin: 8px 0;
}
.wa-day span {
	display: inline-block;
	background: #fff;
	color: #54656f;
	font-size: 12px;
	padding: 3px 10px;
	border-radius: 8px;
	box-shadow: 0 1px 0.5px rgba(11, 20, 26, 0.13);
}
.wa-row {
	display: flex;
	margin-bottom: 4px;
}
.wa-row--out {
	justify-content: flex-end;
}
.wa-row--in {
	justify-content: flex-start;
}
.wa-bubble {
	position: relative;
	max-width: 65%;
	min-width: 72px;
	padding: 6px 9px 4px;
	border-radius: 8px;
	font-size: 14px;
	line-height: 1.4;
	color: #111b21;
	word-break: break-word;
	white-space: pre-line;
	box-shadow: 0 1px 0.5px rgba(11, 20, 26, 0.13);
}
.wa-bubble--in {
	background: #fff;
	border-top-left-radius: 0;
}
.wa-bubble--out {
	background: #d9fdd3;
	border-top-right-radius: 0;
}
/* codina del fumetto */
.wa-bubble--in::before,
.wa-bubble--out::before {
	content: '';
	position: absolute;
	top: 0;
	width: 0;
	height: 0;
	border-top: 8px solid;
}
.wa-bubble--in::before {
	left: -8px;
	border-top-color: #fff;
	border-left: 8px solid transparent;
}
.wa-bubble--out::before {
	right: -8px;
	border-top-color: #d9fdd3;
	border-right: 8px solid transparent;
}
.wa-template {
	font-style: italic;
	color: #54656f;
}
.wa-meta {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	font-size: 11px;
	color: #667781;
	margin-top: 2px;
	white-space: nowrap;
}
.wa-media ::v-deep img,
.wa-media ::v-deep video {
	display: block;
	max-width: 100% !important;
	width: auto;
	height: auto;
	border-radius: 6px;
}
.wa-media ::v-deep audio {
	max-width: 100%;
}
.wa-media ::v-deep p {
	margin: 4px 0 0;
}
.wa-empty {
	text-align: center;
	color: #667781;
	font-size: 13px;
	padding: 24px 0;
}
.wa-composer {
	background: #f0f2f5;
	padding: 6px 8px;
	border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.wa-composer-row {
	display: flex;
	align-items: flex-end;
	gap: 4px;
}
.wa-tool {
	flex: 0 0 auto;
	margin-bottom: 2px;
}
.wa-input {
	flex: 1 1 auto;
	min-width: 0;
}
.wa-input ::v-deep .v-input__slot {
	border-radius: 20px !important;
	padding: 0 14px !important;
	min-height: 40px;
}
.wa-input ::v-deep textarea {
	max-height: 140px;
	overflow-y: auto !important;
	line-height: 1.4;
	padding: 9px 0 !important;
	margin: 0 !important;
}
.wa-send {
	flex: 0 0 auto;
}
.wa-attachs {
	padding: 2px 4px 4px;
}
.wa-chip-text {
	max-width: 200px;
	display: inline-block;
}

@media (max-width: 768px) {
	.wa-chat {
		margin: 0 -4px;
	}
	.wa-chat-body {
		max-height: 60vh;
		padding: 8px;
	}
	.wa-bubble {
		max-width: 82%;
	}
	.wa-chip-text {
		max-width: 150px;
	}
}
</style>
